// Proportional Layout System
// Uses physical screen space fractions instead of fixed pixels

// Device size breakpoints
export const DEVICE_BREAKPOINTS = {
  PHONE_MAX: 768,    // < 768px is phone
  TABLET_MAX: 1024   // >= 1024px is tablet+
};

// Device types based on screen width
export const DEVICE_TYPES = {
  PHONE: 'phone',
  TABLET: 'tablet'  // Tablet and larger
};

// Section identifiers
export const SECTIONS = {
  YEAR_DOTS: 'yearDots',
  WEEK_VIEW: 'weekView',
  STATS: 'stats'
};

// Proportional layout definitions
// All values are fractions of available screen height (after header)
export const SECTION_PROPORTIONS = {
  [DEVICE_TYPES.PHONE]: {
    [SECTIONS.YEAR_DOTS]: 1/4,   // 25% - Year in dots
    [SECTIONS.WEEK_VIEW]: 3/4,   // 75% - Current week
    [SECTIONS.STATS]: 0          // Hidden on phone
  },
  [DEVICE_TYPES.TABLET]: {
    [SECTIONS.YEAR_DOTS]: 1/4,   // 25% - Year in dots
    [SECTIONS.WEEK_VIEW]: 2/4,   // 50% - Current week
    [SECTIONS.STATS]: 1/4        // 25% - Stats section
  }
};

/**
 * Detect device type based on screen width
 */
export const detectDeviceType = (width) => {
  if (width < DEVICE_BREAKPOINTS.PHONE_MAX) {
    return DEVICE_TYPES.PHONE;
  }
  return DEVICE_TYPES.TABLET;
};

/**
 * Get section proportions for a device type
 */
export const getSectionProportions = (deviceType) => {
  return SECTION_PROPORTIONS[deviceType] || SECTION_PROPORTIONS[DEVICE_TYPES.TABLET];
};

/**
 * Check if stats section should be visible
 */
export const isStatsVisible = (deviceType) => {
  const proportions = getSectionProportions(deviceType);
  return proportions[SECTIONS.STATS] > 0;
};

/**
 * Calculate actual pixel heights for sections given available height
 * @param {string} deviceType - 'phone' or 'tablet'
 * @param {number} availableHeight - Height in pixels available for sections
 * @param {number} gap - Gap between sections in pixels (optional)
 * @returns {Object} Heights for each section in pixels
 */
export const calculateSectionHeights = (deviceType, availableHeight, gap = 0) => {
  const proportions = getSectionProportions(deviceType);
  const visibleSections = Object.values(proportions).filter(p => p > 0).length;
  const totalGapSpace = gap * (visibleSections - 1);
  const heightForSections = availableHeight - totalGapSpace;

  return {
    [SECTIONS.YEAR_DOTS]: Math.floor(heightForSections * proportions[SECTIONS.YEAR_DOTS]),
    [SECTIONS.WEEK_VIEW]: Math.floor(heightForSections * proportions[SECTIONS.WEEK_VIEW]),
    [SECTIONS.STATS]: Math.floor(heightForSections * proportions[SECTIONS.STATS])
  };
};

/**
 * Get flex values for CSS flex-grow
 * Returns proportions multiplied by 4 for cleaner integer values
 */
export const getFlexValues = (deviceType) => {
  const proportions = getSectionProportions(deviceType);
  return {
    [SECTIONS.YEAR_DOTS]: proportions[SECTIONS.YEAR_DOTS] * 4,  // 1
    [SECTIONS.WEEK_VIEW]: proportions[SECTIONS.WEEK_VIEW] * 4,  // 3 or 2
    [SECTIONS.STATS]: proportions[SECTIONS.STATS] * 4           // 0 or 1
  };
};

const proportionalLayoutExports = {
  DEVICE_BREAKPOINTS,
  DEVICE_TYPES,
  SECTIONS,
  SECTION_PROPORTIONS,
  detectDeviceType,
  getSectionProportions,
  isStatsVisible,
  calculateSectionHeights,
  getFlexValues
};

export default proportionalLayoutExports;
